import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Slider from 'react-slick';
import CountUp from 'react-countup';
import { useInView } from 'react-intersection-observer';
import {
  FiTruck, FiShield, FiUsers, FiPackage, FiBarChart2, FiAward,
  FiCheckCircle, FiArrowRight, FiPhone, FiSend, FiMail
} from 'react-icons/fi';
import { FaHandshake, FaStar, FaRegStar, FaWhatsapp } from 'react-icons/fa';

import testimonials from '../data/testimonials.json';
import productsData from '../data/products.json';

/* ── 1. Hero Banner ── */
function HeroBanner() {
  const pills = [
    { emoji: '🌶️', label: 'Spices & Masalas' },
    { emoji: '🍫', label: 'Chocolates' },
    { emoji: '🌾', label: 'Rice & Grains' },
    { emoji: '🍎', label: 'Fresh Fruits' },
    { emoji: '🥦', label: 'Vegetables' },
    { emoji: '🛒', label: 'Grocery FMCG' },
  ];
  return (
    <section className="hero-section">
      <div className="hero-bg-shapes">
        <div className="hero-shape hero-shape-1" />
        <div className="hero-shape hero-shape-2" />
        <div className="hero-shape hero-shape-3" />
        <div className="hero-grid-lines" />
      </div>
      <div className="container">
        <div className="row align-items-center">
          <div className="col-lg-7" data-aos="fade-right">
            <div className="hero-content">
              <div className="hero-badge">
                <span className="dot" />
                Authorized Aachi Masala Distributor · FSSAI Certified
              </div>
              <h1 className="hero-heading">
                Your Trusted<br />
                <span className="highlight">FMCG Distribution</span><br />
                Partner
              </h1>
              <p className="hero-subtitle">
                Distributing premium Spices, Chocolates, Rice, Fresh Fruits &amp; Vegetables, and Grocery Essentials across Tamil Nadu — from source to shelf, with care.
              </p>
              <div className="hero-category-pills">
                {pills.map(p => (
                  <div className="hero-cat-pill" key={p.label}>
                    <span>{p.emoji}</span> {p.label}
                  </div>
                ))}
              </div>
              <div className="hero-ctas" style={{ marginTop: 32 }}>
                <Link to="/become-dealer" className="btn-primary-c" style={{ fontSize: '1rem', padding: '15px 36px' }}>
                  <FaHandshake size={18} /> Become a Partner
                </Link>
                <Link to="/contact" className="btn-outline-white" style={{ fontSize: '1rem', padding: '13px 34px' }}>
                  <FiPhone size={16} /> Contact Us
                </Link>
              </div>
              <div className="hero-stats">
                {[
                  { number: '500+', label: 'Retail Partners' },
                  { number: '6', label: 'Product Categories' },
                  { number: '10+', label: 'Districts Served' },
                  { number: '100%', label: 'FSSAI Certified' },
                ].map(s => (
                  <div className="hero-stat" key={s.label}>
                    <div className="hero-stat-number">{s.number}</div>
                    <div className="hero-stat-label">{s.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="col-lg-5 d-none d-lg-flex justify-content-center" data-aos="fade-left" data-aos-delay="200">
            <div className="hero-card-stack">
              <div className="hero-main-card">
                <div style={{ textAlign: 'center', marginBottom: 20 }}>
                  <div style={{ fontSize: '3rem', marginBottom: 8 }}>🏪</div>
                  <div style={{ fontFamily: 'Poppins', fontWeight: 800, fontSize: '1.25rem', color: '#fff' }}>6 Product Categories</div>
                  <div style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.55)', marginTop: 4 }}>Spices · Chocolates · Rice · Fruits · Vegetables · Grocery</div>
                </div>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
                  {['🌶️ Spices', '🍫 Choc', '🌾 Rice', '🍎 Fruits', '🥦 Veggies', '🛒 Grocery'].map(p => (
                    <span key={p} style={{ background: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.82)', padding: '5px 12px', borderRadius: 20, fontSize: '0.76rem', fontWeight: 600, border: '1px solid rgba(255,255,255,0.12)' }}>
                      {p}
                    </span>
                  ))}
                </div>
                <div style={{ marginTop: 20, background: 'rgba(243,156,18,0.15)', border: '1px solid rgba(243,156,18,0.3)', borderRadius: 10, padding: '10px 14px', textAlign: 'center' }}>
                  <span style={{ color: '#f39c12', fontWeight: 700, fontSize: '0.82rem' }}>✅ FSSAI Certified · Authorized Aachi Distributor</span>
                </div>
              </div>
              <div className="hero-floating-card top-right">
                <div className="hfc-icon" style={{ background: '#e8f5e9' }}>
                  <FiTruck style={{ color: '#28A745', fontSize: '1.2rem' }} />
                </div>
                <div className="hfc-text">
                  <strong>Fast Delivery</strong>
                  <span>Pan Tamil Nadu</span>
                </div>
              </div>
              <div className="hero-floating-card bottom-left">
                <div className="hfc-icon" style={{ background: '#fef5e7' }}>
                  <FiShield style={{ color: '#F39C12', fontSize: '1.2rem' }} />
                </div>
                <div className="hfc-text">
                  <strong>FSSAI Certified</strong>
                  <span>100% Authentic</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ── 2. About with 4 Pillars ── */
function AboutSection() {
  const pillars = [
    { emoji: '🌱', title: 'Quality Sourcing', desc: 'Direct tie-ups with manufacturers and certified farms for authentic, premium-grade products.', bg: '#e8f5e9' },
    { emoji: '✅', title: 'FSSAI Certified', desc: 'All products we distribute comply with FSSAI food safety standards and quality norms.', bg: '#e8f1fb' },
    { emoji: '🚚', title: 'Reliable Logistics', desc: 'Dedicated delivery fleet ensuring timely supply to all our distribution partners.', bg: '#fef5e7' },
    { emoji: '🤝', title: 'Partner-First', desc: 'Your growth is our mission. We provide dedicated support, training, and competitive margins.', bg: '#f5eef8' },
  ];
  return (
    <section style={{ padding: '90px 0' }}>
      <div className="container">
        <div className="row align-items-center g-5">
          <div className="col-lg-5" data-aos="fade-right">
            <div style={{ background: 'var(--gradient-primary)', borderRadius: 24, padding: 44, position: 'relative', overflow: 'hidden', minHeight: 360 }}>
              <div style={{ position: 'absolute', top: -40, right: -40, width: 200, height: 200, background: 'rgba(255,255,255,0.05)', borderRadius: '50%' }} />
              <div style={{ position: 'absolute', bottom: -30, left: -30, width: 160, height: 160, background: 'rgba(255,255,255,0.05)', borderRadius: '50%' }} />
              <div style={{ position: 'relative', zIndex: 1 }}>
                <div style={{ fontSize: '3rem', marginBottom: 14 }}>🏭</div>
                <h3 style={{ color: '#fff', fontWeight: 900, fontSize: '1.6rem', marginBottom: 10 }}>Avivan Enterprises</h3>
                <p style={{ color: 'rgba(255,255,255,0.75)', lineHeight: 1.75, fontSize: '0.9rem', marginBottom: 24 }}>
                  A growing FMCG distribution company committed to quality, trust, and a passion for authentic Indian food — serving businesses across Tamil Nadu.
                </p>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  {[
                    { num: '500+', lbl: 'Partners' },
                    { num: '5+', lbl: 'Years' },
                    { num: '10+', lbl: 'Districts' },
                    { num: '6', lbl: 'Categories' },
                  ].map(s => (
                    <div key={s.lbl} style={{ background: 'rgba(255,255,255,0.1)', borderRadius: 10, padding: '12px 16px', border: '1px solid rgba(255,255,255,0.12)', textAlign: 'center', minWidth: 72, flex: '1 1 auto' }}>
                      <div style={{ fontSize: '1.4rem', fontWeight: 900, color: 'var(--accent)', fontFamily: 'Poppins, sans-serif' }}>{s.num}</div>
                      <div style={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.6)', marginTop: 2, textTransform: 'uppercase', letterSpacing: '1px' }}>{s.lbl}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <div className="col-lg-7" data-aos="fade-left">
            <span className="section-badge"><FiPackage size={12} /> Who We Are</span>
            <h2 className="section-title mt-2">Quality, Trust &amp; a Passion for <span>Authentic Indian Food</span></h2>
            <p style={{ color: 'var(--text-muted)', lineHeight: 1.8, fontSize: '0.95rem', marginBottom: 28 }}>
              Avivan Enterprises is a fast-growing FMCG distribution company focused on building a reliable, scalable supply chain across Tamil Nadu. Currently an authorized distributor of <strong>Aachi Masala</strong>, we are rapidly expanding into Chocolates, Rice, Fresh Produce, and Grocery categories to become a one-stop FMCG distribution partner.
            </p>
            <div className="pillar-grid">
              {pillars.map((p, i) => (
                <div className="pillar-item" key={p.title} data-aos="fade-up" data-aos-delay={i * 60}>
                  <div className="pillar-icon" style={{ background: p.bg }}>
                    <span>{p.emoji}</span>
                  </div>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: '0.93rem', color: 'var(--dark)', marginBottom: 4 }}>{p.title}</div>
                    <div style={{ fontSize: '0.81rem', color: 'var(--text-muted)', lineHeight: 1.55 }}>{p.desc}</div>
                  </div>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginTop: 28 }}>
              <Link to="/about" className="btn-primary-c">Learn More <FiArrowRight size={15} /></Link>
              <Link to="/become-dealer" className="btn-outline-c">Join Our Network</Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ── 3. Six Categories ── */
function ProductCategories() {
  const { categories } = productsData;
  return (
    <section style={{ padding: '90px 0', background: 'var(--light)' }}>
      <div className="container">
        <div className="section-header" data-aos="fade-up">
          <span className="section-badge"><FiPackage size={12} /> Product Portfolio</span>
          <h2 className="section-title">Six Categories, <span>Infinite Quality</span></h2>
          <p className="section-subtitle">From premium spices to fresh produce — a complete FMCG distribution portfolio for every business type</p>
          <div className="divider-bar" />
        </div>
        <div className="cat-cards-grid">
          {categories.map((cat, i) => (
            <div className="cat-card" key={cat.id} data-aos="fade-up" data-aos-delay={i * 70}>
              <div className="cat-card-header" style={{ borderBottom: `2px solid ${cat.color}18` }}>
                <div className="cat-emoji-box" style={{ background: cat.colorLight }}>
                  <span>{cat.emoji}</span>
                </div>
                <div>
                  <h4 className="cat-card-title">{cat.name}</h4>
                  <div style={{ marginTop: 5 }}>
                    {cat.status === 'active'
                      ? <span className="badge-active">Distributing Now</span>
                      : <span className="badge-coming-soon">Coming Soon</span>}
                  </div>
                </div>
              </div>
              <div className="cat-card-body">
                <p className="cat-card-desc">{cat.desc}</p>
                <ul className="cat-product-list">
                  {cat.products.slice(0, 4).map(p => (
                    <li className="cat-product-item" key={p.id}>
                      <span className="cat-item-emoji">{p.emoji}</span>{p.name}
                    </li>
                  ))}
                </ul>
                <Link to="/products" className="cat-enquire-btn">
                  Enquire About Products <FiArrowRight size={13} />
                </Link>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── 4. Why Choose Us — numbered ── */
function WhyChooseUs() {
  const reasons = [
    { title: 'Direct Brand Partnerships', desc: 'Authorized distribution agreements with trusted FMCG brands — ensuring authentic products and competitive pricing at every level.' },
    { title: 'FSSAI Certified Products', desc: 'Every product we distribute meets FSSAI food safety standards. Quality compliance is non-negotiable for us.' },
    { title: 'Reliable Supply Chain', desc: 'Robust logistics from manufacturer to your shelf — no middlemen, no delays, zero stock-outs guaranteed.' },
    { title: 'Competitive Dealer Margins', desc: 'Best-in-market margins with volume incentives, prompt credit facilities, and tier-based benefits for growing partners.' },
    { title: 'Dedicated Account Manager', desc: 'Every dealer gets a personal account manager for seamless order management, support, and business growth planning.' },
    { title: 'Fast Turnaround Delivery', desc: 'Same-day or next-day delivery within primary zones. Scheduled weekly supplies for all registered partners.' },
  ];
  return (
    <section style={{ padding: '90px 0' }}>
      <div className="container">
        <div className="section-header" data-aos="fade-up">
          <span className="section-badge"><FiAward size={12} /> Our Advantages</span>
          <h2 className="section-title">Built on Trust &amp; <span>Excellence</span></h2>
          <p className="section-subtitle">Six reasons why retailers, hotels, institutions, and wholesalers across Tamil Nadu choose Avivan Enterprises</p>
          <div className="divider-bar" />
        </div>
        <div className="row g-4">
          {reasons.map((r, i) => (
            <div className="col-lg-4 col-md-6" key={r.title} data-aos="fade-up" data-aos-delay={i * 70}>
              <div className="why-numbered-card">
                <div className="why-num">{String(i + 1).padStart(2, '0')}</div>
                <h4 className="why-num-title">{r.title}</h4>
                <p className="why-num-desc">{r.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── 5. Who We Serve ── */
function WhoWeServe() {
  const segments = [
    { emoji: '🏪', title: 'Retail Stores' },
    { emoji: '🏬', title: 'Supermarkets' },
    { emoji: '🏭', title: 'Wholesalers' },
    { emoji: '🏨', title: 'Hotels' },
    { emoji: '🍽️', title: 'Restaurants' },
    { emoji: '🍱', title: 'Caterers' },
    { emoji: '🎓', title: 'Institutions' },
    { emoji: '🏢', title: 'Corporate Canteens' },
    { emoji: '✈️', title: 'Export Buyers' },
    { emoji: '🛒', title: 'Online Sellers' },
  ];
  return (
    <section style={{ padding: '90px 0', background: 'var(--light)' }}>
      <div className="container">
        <div className="section-header" data-aos="fade-up">
          <span className="section-badge"><FiUsers size={12} /> Our Clients</span>
          <h2 className="section-title">Trusted Across India's <span>Food Ecosystem</span></h2>
          <p className="section-subtitle">From five-star hotel kitchens to neighbourhood kirana stores — we supply them all</p>
          <div className="divider-bar" />
        </div>
        <div className="row g-3">
          {segments.map((s, i) => (
            <div className="col-lg-2 col-md-3 col-4" key={s.title} data-aos="fade-up" data-aos-delay={i * 45}>
              <div className="serve-card">
                <div style={{ fontSize: '2rem', marginBottom: 10 }}>{s.emoji}</div>
                <div className="serve-title">{s.title}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── 6. Featured Product — Aachi Masala ── */
function FeaturedProduct() {
  const fp = productsData.featuredProduct;
  return (
    <section className="featured-product-section">
      <div className="fp-bg-circle fp-bg-circle-1" />
      <div className="fp-bg-circle fp-bg-circle-2" />
      <div className="container" style={{ position: 'relative', zIndex: 1 }}>
        <div className="row align-items-center g-5">
          <div className="col-lg-6" data-aos="fade-right">
            <span className="fp-badge">🌶️ Currently Distributing</span>
            <h2 className="fp-heading">
              Aachi Masala —<br /><span>India's No.1</span> Spice Brand
            </h2>
            <p className="fp-desc">{fp.desc}</p>
            <ul className="fp-highlights">
              {fp.highlights.map(h => (
                <li key={h}>
                  <FiCheckCircle size={15} className="fp-check" />
                  {h}
                </li>
              ))}
            </ul>
            <div className="fp-spec-grid">
              {fp.specs.map(s => (
                <div className="fp-spec-item" key={s.label}>
                  <div className="fp-spec-label">{s.label}</div>
                  <div className="fp-spec-value">{s.value}</div>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginTop: 28 }}>
              <a href="tel:+919876543210" className="btn-accent-c" style={{ fontSize: '1rem', padding: '14px 36px' }}>
                <FiPhone size={16} /> Order Now
              </a>
              <a href="https://wa.me/919876543210" target="_blank" rel="noopener noreferrer" className="btn-outline-white" style={{ fontSize: '1rem', padding: '12px 34px' }}>
                <FaWhatsapp size={18} /> WhatsApp Us
              </a>
            </div>
          </div>
          <div className="col-lg-6 d-none d-lg-block" data-aos="fade-left" data-aos-delay="150">
            <div className="fp-visual">
              <div style={{ fontSize: '5rem', marginBottom: 12 }}>🌶️</div>
              <div style={{ fontFamily: 'Poppins', fontWeight: 900, fontSize: '1.4rem', color: '#fff', marginBottom: 4 }}>Aachi Masala</div>
              <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.78rem', marginBottom: 22 }}>Complete Range · Authorized Distributor</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center', marginBottom: 22 }}>
                {['🫙 Sambar', '🌶️ Chilli', '🌿 Coriander', '🟡 Turmeric', '🍗 Chicken', '🍖 Mutton', '🫚 Garam', '🍚 Biryani'].map(p => (
                  <span key={p} style={{ background: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.82)', padding: '6px 13px', borderRadius: 20, fontSize: '0.76rem', fontWeight: 600, border: '1px solid rgba(255,255,255,0.12)' }}>
                    {p}
                  </span>
                ))}
              </div>
              <div style={{ background: 'rgba(243,156,18,0.12)', border: '1px solid rgba(243,156,18,0.25)', borderRadius: 12, padding: '13px 18px' }}>
                <div style={{ color: '#f39c12', fontWeight: 700, fontSize: '0.85rem' }}>✅ 100% Authentic · FSSAI Certified · Pan Tamil Nadu Supply</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ── 7. Statistics ── */
function Statistics() {
  const { ref, inView } = useInView({ threshold: 0.3, triggerOnce: true });
  const stats = [
    { end: 500, suffix: '+', label: 'Retail Partners' },
    { end: 6, suffix: '', label: 'Product Categories' },
    { end: 10, suffix: '+', label: 'Districts Covered' },
    { end: 100, suffix: '%', label: 'Quality Assured' },
    { end: 20, suffix: '+', label: 'Categories by 2027' },
  ];
  return (
    <section className="stats-section" ref={ref}>
      <div className="container">
        <div className="section-header" data-aos="fade-up" style={{ marginBottom: 48 }}>
          <span className="section-badge" style={{ background: 'rgba(255,255,255,0.12)', color: '#fff', border: '1px solid rgba(255,255,255,0.2)' }}>
            <FiBarChart2 size={12} /> Our Impact
          </span>
          <h2 className="section-title" style={{ color: '#fff' }}>
            Numbers That <span style={{ color: 'var(--accent)' }}>Speak for Themselves</span>
          </h2>
          <div className="divider-bar" />
        </div>
        <div className="row g-4 align-items-center justify-content-center">
          {stats.map((s, i) => (
            <React.Fragment key={s.label}>
              <div className="col-lg-2 col-md-4 col-6" data-aos="fade-up" data-aos-delay={i * 80}>
                <div className="stat-item">
                  <div className="stat-number">
                    {inView ? <CountUp end={s.end} duration={2.5} separator="," /> : '0'}
                    <span className="stat-suffix">{s.suffix}</span>
                  </div>
                  <div className="stat-label">{s.label}</div>
                </div>
              </div>
              {i < stats.length - 1 && (
                <div className="col-auto d-none d-lg-flex">
                  <div className="stat-divider" />
                </div>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── 8. Partner Form — "Let's Partner Together" ── */
function PartnerForm() {
  const { categories } = productsData;
  const emptyForm = { name: '', phone: '', email: '', iam: '', product: '', message: '' };
  const [form, setForm] = useState(emptyForm);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));
  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitting(true);
    setTimeout(() => { setSubmitting(false); setSubmitted(true); }, 1500);
  };

  const contactLinks = [
    { icon: <FiPhone size={16} />, label: '+91 98765 43210', sub: 'Mon–Sat, 9AM–6PM', href: 'tel:+919876543210', bg: '#e8f1fb', color: '#0F4C81' },
    { icon: <FaWhatsapp size={16} />, label: 'WhatsApp Us', sub: 'Quick response guaranteed', href: 'https://wa.me/919876543210', bg: '#d4edda', color: '#28A745' },
    { icon: <FiMail size={16} />, label: 'info@avivanenterprises.com', sub: 'Send us your enquiry', href: 'mailto:info@avivanenterprises.com', bg: '#fef5e7', color: '#F39C12' },
  ];

  return (
    <section className="partner-form-section">
      <div className="container">
        <div className="row g-5 align-items-center">
          <div className="col-lg-5" data-aos="fade-right">
            <span className="section-badge"><FaHandshake size={12} /> Partnership</span>
            <h2 className="section-title mt-2">Let's Partner <span>Together</span></h2>
            <p style={{ color: 'var(--text-muted)', lineHeight: 1.8, marginBottom: 28, fontSize: '0.95rem' }}>
              Whether you're a retailer, wholesaler, hotel, caterer, or institution — Avivan Enterprises has the right distribution partnership for your business.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {contactLinks.map(c => (
                <a key={c.label} href={c.href} target={c.href.startsWith('http') ? '_blank' : undefined} rel="noopener noreferrer" className="partner-contact-link">
                  <div className="pcl-icon" style={{ background: c.bg, color: c.color }}>{c.icon}</div>
                  <div>
                    <div className="pcl-label">{c.label}</div>
                    <div className="pcl-sub">{c.sub}</div>
                  </div>
                </a>
              ))}
            </div>
          </div>
          <div className="col-lg-7" data-aos="fade-left">
            <div className="partner-form-card">
              <h4 style={{ fontWeight: 800, fontSize: '1.2rem', marginBottom: 4 }}>Send Us an Enquiry</h4>
              <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginBottom: 26 }}>Fill out the form and our team will get back to you within 24 hours.</p>
              {submitted ? (
                <div style={{ background: '#d4edda', border: '1px solid #28A745', borderRadius: 14, padding: '36px', textAlign: 'center' }}>
                  <div style={{ fontSize: '3rem', marginBottom: 12 }}>✅</div>
                  <h4 style={{ color: '#1e7e34', fontWeight: 800, marginBottom: 8 }}>Enquiry Sent Successfully!</h4>
                  <p style={{ color: '#155724', margin: '0 0 16px', fontSize: '0.9rem', lineHeight: 1.6 }}>Our team will contact you within 24 business hours.</p>
                  <button className="btn-primary-c" onClick={() => { setForm(emptyForm); setSubmitted(false); }} style={{ fontSize: '0.88rem' }}>
                    Send Another Enquiry
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit}>
                  <div className="row">
                    <div className="col-md-6">
                      <div className="form-group-c">
                        <label className="form-label-c">Full Name *</label>
                        <input className="form-control-c" name="name" placeholder="Your full name" value={form.name} onChange={handleChange} required />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-group-c">
                        <label className="form-label-c">Phone Number *</label>
                        <input className="form-control-c" name="phone" placeholder="10-digit mobile number" value={form.phone} onChange={handleChange} required />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-group-c">
                        <label className="form-label-c">Email Address</label>
                        <input className="form-control-c" name="email" type="email" placeholder="your@email.com" value={form.email} onChange={handleChange} />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-group-c">
                        <label className="form-label-c">I am a... *</label>
                        <select className="form-select-c" name="iam" value={form.iam} onChange={handleChange} required>
                          <option value="">Select your business type</option>
                          {['Retailer', 'Wholesaler', 'Supermarket / Modern Trade', 'Hotel', 'Restaurant', 'Caterer', 'Marriage Hall', 'Institution / Canteen', 'Online / E-Commerce Seller', 'Export Buyer', 'Other'].map(t => (
                            <option key={t} value={t}>{t}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="col-12">
                      <div className="form-group-c">
                        <label className="form-label-c">Product of Interest *</label>
                        <select className="form-select-c" name="product" value={form.product} onChange={handleChange} required>
                          <option value="">Select a product category</option>
                          {categories.map(c => (
                            <option key={c.id} value={c.name}>{c.emoji} {c.name}</option>
                          ))}
                          <option value="Multiple Categories">All / Multiple Categories</option>
                        </select>
                      </div>
                    </div>
                    <div className="col-12">
                      <div className="form-group-c">
                        <label className="form-label-c">Message / Requirements</label>
                        <textarea className="form-control-c" name="message" rows="4" placeholder="Tell us about your business, area, monthly requirement, or any specific needs..." value={form.message} onChange={handleChange} />
                      </div>
                    </div>
                  </div>
                  <div className="form-privacy-note">
                    ℹ️ By submitting you agree to be contacted by our team. No spam, ever.
                  </div>
                  <button type="submit" className="btn-primary-c w-100" style={{ fontSize: '1rem', padding: 15, justifyContent: 'center' }} disabled={submitting}>
                    {submitting
                      ? <><span className="spinner-border spinner-border-sm me-2" />Sending...</>
                      : <><FiSend size={16} /> Send Enquiry</>}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ── 9. Testimonials ── */
function Testimonials() {
  const settings = {
    dots: true, infinite: true, speed: 500,
    slidesToShow: 2, slidesToScroll: 1,
    autoplay: true, autoplaySpeed: 4000, pauseOnHover: true,
    responsive: [{ breakpoint: 991, settings: { slidesToShow: 1 } }],
  };
  return (
    <section className="testimonials-section">
      <div className="container">
        <div className="section-header" data-aos="fade-up">
          <span className="section-badge"><FaStar size={12} /> Client Reviews</span>
          <h2 className="section-title">What Our <span>Partners Say</span></h2>
          <p className="section-subtitle">Trusted by hundreds of retailers, wholesalers, and food businesses across Tamil Nadu</p>
          <div className="divider-bar" />
        </div>
        <div data-aos="fade-up" data-aos-delay="100">
          <Slider {...settings}>
            {testimonials.map(t => (
              <div key={t.id}>
                <div className="testimonial-card">
                  <div className="testimonial-stars">
                    {Array.from({ length: t.rating }).map((_, i) => <FaStar key={i} />)}
                    {Array.from({ length: 5 - t.rating }).map((_, i) => <FaRegStar key={i} />)}
                  </div>
                  <p className="testimonial-text">"{t.text}"</p>
                  <div className="testimonial-author">
                    <div className="author-avatar" style={{ background: t.color }}>{t.initials}</div>
                    <div>
                      <div className="author-name">{t.name}</div>
                      <div className="author-role">{t.role}</div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </Slider>
        </div>
      </div>
    </section>
  );
}

/* ── 10. FAQ ── */
function FAQ() {
  const faqs = [
    { q: 'How do I become a dealer or distributor for Avivan Enterprises?', a: 'Apply through our "Become a Dealer" page or use the enquiry form above. Our partnership team will review your application and contact you within 48 hours to discuss territory availability and terms.' },
    { q: 'What products does Avivan Enterprises currently distribute?', a: 'We currently distribute the complete Aachi Masala range including Sambar Powder, Chilli Powder, Coriander Powder, Turmeric Powder, Garam Masala, Chicken Masala, Mutton Masala, and Biryani Masala. We are expanding into Chocolates, Rice, Fresh Produce, and Grocery categories soon.' },
    { q: 'What is the minimum order quantity (MOQ) for dealers?', a: 'The MOQ varies by partnership tier. Silver partners start from ₹25,000 per order, while Gold and Platinum partners have higher MOQs with better margins. Contact our sales team for detailed pricing.' },
    { q: 'Are all your products FSSAI certified?', a: 'Yes, all products distributed by Avivan Enterprises are FSSAI certified and sourced only from authorized manufacturers. We maintain strict quality checks at every stage of the supply chain.' },
    { q: 'Which areas do you currently cover for distribution?', a: 'We currently serve multiple districts across Tamil Nadu with primary focus on Aachi Masala distribution. We are actively expanding our network to cover more regions in South India and beyond.' },
    { q: 'Do you supply to hotels, restaurants, and institutions?', a: 'Absolutely! We have dedicated HoReCa supply solutions for hotels, restaurants, caterers, marriage halls, institutional canteens, and corporate kitchens with bulk pricing and flexible delivery schedules.' },
    { q: 'Are you planning to add more product categories?', a: 'Yes! Our vision is to become a multi-category FMCG distributor covering Chocolates & Confectionery, Rice & Grains, Fresh Fruits, Fresh Vegetables, and Grocery Essentials — targeting 20+ categories by 2027.' },
  ];
  return (
    <section className="faq-section">
      <div className="container">
        <div className="section-header" data-aos="fade-up">
          <span className="section-badge">❓ Common Questions</span>
          <h2 className="section-title">Frequently Asked <span>Questions</span></h2>
          <p className="section-subtitle">Everything you need to know about partnering with Avivan Enterprises</p>
          <div className="divider-bar" />
        </div>
        <div className="row justify-content-center">
          <div className="col-lg-9">
            <div className="accordion faq-accordion" id="faqAccordion" data-aos="fade-up" data-aos-delay="100">
              {faqs.map((f, i) => (
                <div className="accordion-item" key={i}>
                  <h2 className="accordion-header">
                    <button className={`accordion-button ${i !== 0 ? 'collapsed' : ''}`} type="button" data-bs-toggle="collapse" data-bs-target={`#faq${i}`}>
                      {f.q}
                    </button>
                  </h2>
                  <div id={`faq${i}`} className={`accordion-collapse collapse ${i === 0 ? 'show' : ''}`} data-bs-parent="#faqAccordion">
                    <div className="accordion-body">{f.a}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ── Main Page ── */
export default function Home() {
  return (
    <>
      <Helmet>
        <title>Avivan Enterprises | FMCG Distribution &amp; Supply Chain Partner India</title>
        <meta name="description" content="Avivan Enterprises — Your Trusted FMCG Distribution Partner. Authorized Aachi Masala distributor delivering Spices, Chocolates, Rice, Fresh Produce and Grocery to retailers, hotels, and institutions across Tamil Nadu." />
      </Helmet>
      <HeroBanner />
      <AboutSection />
      <ProductCategories />
      <WhyChooseUs />
      <WhoWeServe />
      <FeaturedProduct />
      <Statistics />
      <PartnerForm />
      <Testimonials />
      <FAQ />
    </>
  );
}
